import { eventBus } from 'app/eventBus';
import { PiEyeDuotone } from 'react-icons/pi';
import NotificationService from 'app/admin/notification/service/NotificationService';
import { useLocation } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import { useStrictMode } from 'react-konva';
import _ from '@lodash';
import FusePageCarded from '@fuse/core/FusePageCarded';
import { Typography, Icon, Button, IconButton, TextField, FormControl, InputLabel, Select, MenuItem, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useParams, withRouter, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowDropDown, Dashboard, NavigateBefore } from '@material-ui/icons';
// import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { showMessage } from 'app/store/fuse/messageSlice';
import PageHeaderBackButton from 'modules/ui/component/PageHeaderBackButton';
import AspectService from 'app/admin/aspect/service/AspectService';
import OptimizationCaseTaskEditService from 'app/admin/taskManager/service/OptimizationCaseTaskEditService';
import CalculateTaskEditService from 'app/admin/taskManager/service/CalculateTaskEditService';
import TaskManagerEditService from 'app/admin/taskManager/service/TaskManagerEditService';
import WhatIfView from '../component/WhatIfView';
import { useWhatIf } from '../component/WhatIfProvider';
import WhatIfActions from '../component/WhatIfActions';
import WhatIfViewModeButton from '../component/WhatIfViewModeButton';
import WhatIfOptimizationCaseButton from '../component/WhatIfOptimizationCaseButton';
import WhatIfEditService from '../service/WhatIfEditService';
import WhatIfDeviceButtons from '../component/WhatIfDeviceButtons';
import CircleItemProperties from '../circle/CircleItemProperties';
import RectItemProperties from '../rect/RectItemProperties';
import DeviceItemProperties from '../device/DeviceItemProperties';
import LabelItemProperties from '../label/LabelItemProperties';
import WhatIfCalculate from '../component/WhatIfCalculate';
import WhatIfAutoConnect from '../component/WhatIfAutoConnect';
import ValueModify from '../label/ValueModify';
import FloatValue from '../label/FloatValue';
import ImageItemProperties from '../image/ImageItemProperties';
import OutputResultDialog from '../label/OutputResultDialog';
import UserGroupService from 'app/admin/userGroup/service/UserGroupService';
import WhatIfExperiment from '../component/WhatIfExperiment';
import WhatIfScenario from '../component/WhatIfScenario';
import SumTableItemProperties from '../sumtable/SumTableItemProperties';
import DocumentItemProperties from '../document/DocumentItemProperties';
import DocumentItemOperator from '../document/DocumentItemOperator';
import NoteItemProperties from '../note/NoteItemProperties';
import NoteItemOperator from '../note/NoteItemOperator';
import WhatIfStateButton from '../component/WhatIfStateButton';
import SumTableDialog from '../sumtable/SumTableDialog';
import ContainerItemProperties from '../container/ContainerItemProperties';
import FigurePathComponent from '../component/FigurePathComponent';
import LinkToWhatIfDialog from '../component/LinkToWhatIfDialog';
import WhatIfProperties from '../component/WhatIfProperties';
import CompareSidebar from '../component/compare/CompareSidebar';
import WhatIfCalculateBackground from '../component/WhatIfCalculateBackground';
import FloatLabelCompare from '../label/FloatLabelCompare';
import UnitOptimizerLogTableService from 'app/admin/unitOptimizerLog/service/UnitOptimizerLogTableService';
import ModelValidationLogTableService from 'app/admin/modelValidationLog/service/ModelValidationLogTableService';
import WhatIfExperimentEditService from '../service/WhatIfExperimentEditService';
import WhatIfScenarioEditService from '../service/WhatIfScenarioEditService';
import { applyScenarioAttributesToExperiment, getScenarioViewMessage } from '../component/WhatIfScenarioUtil';
import TaskManagerLastResultDialog from 'app/admin/taskManager/component/TaskManagerLastResultDialog';

export const paperSize = { width: 10000, height: 10000 };

export const defaultFigureData = {
	name: 'main',
	parent: null,
	stage: { x: (paperSize.width / 2) * -1, y: (paperSize.width / 2) * -1, scale: 1 },
	circle: [],
	device: [],
	document: [],
	image: [],
	label: [],
	line: [],
	note: [],
	rect: [],
	sumTable: [],
	container: []
};

export const defaultData = {
	figure: [defaultFigureData],
	experiment: null,
	properties: null
};

const normalizeSketchFromDto = (sketch, dto) => {
	if (!sketch || !sketch.experiment) return sketch;

	const transformer = dto.transformer ?? null;
	const experiment = {
		...sketch.experiment,
		transformer
	};

	if (transformer == null && Array.isArray(experiment.attributes)) {
		experiment.attributes = experiment.attributes.filter(attribute => attribute.entity !== 'transformer');
	}

	return {
		...sketch,
		experiment
	};
};

const injectTaskStateByType = (sketch, state, entityType) => {
	const input = entityType ? state.state : state.aspect?.completed_state;
	const output = entityType ? (state.output_values || state.output_attributes) : state.aspect?.output_values;

	if (_.isObject(input)) {
		Object.keys(input).forEach(key => {
			const attrib = sketch.experiment.attributes.find(d => d.io === 'INPUT' && d.name === key && (!entityType || d.entity === entityType));
			if (attrib) attrib.value = input[key];
		});
	}

	if (_.isObject(output)) {
		Object.keys(output).forEach(key => {
			const attrib = sketch.experiment.attributes.find(d => d.io === 'OUTPUT' && d.name === key && (!entityType || d.entity === entityType));
			if (attrib) attrib.value = output[key];
		});
	}
};

const injectRawTaskState = (sketch, rawState) => {
	console.log('[injectRawTaskState]', rawState);

	if (!_.isObject(rawState)) return;
	Object.keys(rawState).forEach(key => {
		const value = rawState[key];
		const attribs = sketch.experiment.attributes.filter(d => d.name === key && (d.io === 'INPUT' || d.io === 'OUTPUT'));
		attribs.forEach(attrib => { attrib.value = value; });
	});
};

const formatTaskViewTimestamp = ts => {
	if (!ts) return '';

	return ts
		.replace(/T/, ' ')
		.replace(/\.\d+$/, '');
};

const getTaskViewMessage = taskId => {
	if (!taskId || typeof taskId !== 'string') return null;

	const match = taskId.match(/^(TO|[A-Z])(\d+)(?:&TS(.+))?$/i);
	if (!match) return null;

	const entityType = match[1].toUpperCase();

	const entityTypeLabelMap = {
		O: 'Unit Optimizer',
		M: 'Model Validation',
		T: 'Calculate Task',
		N: 'Calculate Task',
		TO: 'Optimize Task',
	};

	const entityTypeLabel = entityTypeLabelMap[entityType] || entityType;
	const formattedTs = formatTaskViewTimestamp(match[3]);

	if (entityType === 'T' || entityType === 'N' || entityType === 'TO')
		return `You are currently viewing ${entityTypeLabel} result.`;

	if (!formattedTs)
		return null;

	return `You are currently viewing ${entityTypeLabel} ${formattedTs} result.`;
};

const getInjectedResultName = (taskId, fallbackName) => {
	if (fallbackName) return fallbackName;

	const message = getTaskViewMessage(taskId);
	if (!message) return 'Injected state';

	return message
		.replace(/^You are currently viewing\s+/i, '')
		.replace(/\.$/, '');
};

const withStateInjectPayload = (source, stateInject) => {
	if (_.isObject(source) && !Array.isArray(source)) {
		return _.isObject(stateInject)
			? { ...source, state_inject: stateInject }
			: source;
	}

	if (_.isObject(stateInject)) return { state_inject: stateInject };
	return source;
};

const overrideStateInjectWithState = (stateInject, state) => {
	if (!_.isObject(stateInject) || Array.isArray(stateInject)) return stateInject;
	if (!_.isObject(state) || Array.isArray(state)) return stateInject;

	return Object.keys(state).reduce((result, key) => {
		if (Object.prototype.hasOwnProperty.call(result, key)) {
			return {
				...result,
				[key]: state[key]
			};
		}

		return result;
	}, stateInject);
};

const createInjectedResultDialogData = ({ taskId, type, name, data, stateInject }) => {
	const payload = withStateInjectPayload(data, stateInject);

	if (!_.isObject(payload)) return null;

	return {
		item: {
			name: getInjectedResultName(taskId, name),
			type
		},
		data: payload
	};
};

const injectTaskState = (sketch, state, raw) => {
	console.log('[injectTaskState]', state);

	if (state) {
		if (state && raw) injectRawTaskState(sketch, state);
		if (state && state.aspect) injectTaskStateByType(sketch, state.aspect, 'aspect');
		if (state && state.transformer) injectTaskStateByType(sketch, state.transformer, 'transformer');
		if (state && state.best_objective) injectTaskStateByType(sketch, state.best_objective);
		if (state && state.best_objective && state.best_objective.transformer) injectTaskStateByType(sketch, state.best_objective.transformer, 'transformer');
	}

	setTimeout(() => { eventBus.emit('doCalculate'); }, 1500);
};

const mapExperimentDto = experiment => ({
	id: experiment.id,
	name: experiment.name,
	aspect: experiment.aspect ? experiment.aspect.id : null,
	transformer: experiment.transformer ? experiment.transformer.id : null,
	whatIf: experiment.whatIf,
	validity: _.isBoolean(experiment.validity) ? experiment.validity : true,
	attributes: experiment.attributes
});

const useStyles = makeStyles(theme => ({ root: { '& .container': { maxWidth: '100% !important' } } }));

function WhatIfEditPage(props) {
	const user = useSelector(({ auth }) => auth.user);
	// const isOperator = user.roleList.length === 1 && user.roleList[0].title === 'Operator';
	// console.log(user);
	const isOperator = user.role.length === 1 && user.role[0] === 'operator';
	useStrictMode(true);
	const classes = useStyles();
	const dispatch = useDispatch();
	const history = useHistory();
	const { state } = useLocation();
	const { id, taskId } = useParams();

	const { mode, data, setData, figure, setFigure, viewMode, showPropertiesDialog, valueModify, floatValue, outputResultDialog, summaryTableDialog, linkToWhatIf, floatLabelCompare, setViewMessage, setExperiment } = useWhatIf();
	const containerRef = useRef(null);
	const [groupList, setGroupList] = useState(null);
	const [injectedResult, setInjectedResult] = useState(null);
	const [showInjectedResultDialog, setShowInjectedResultDialog] = useState(false);

	const [dto, setDto] = useState(null);
	const [image, setImage] = useState(null);
	const { t } = useTranslation('whatIf');

	useEffect(() => {
		UserGroupService.getListSelection().then(response => {
			setGroupList(_.isArray(response) ? response : []);
		});
		// throw new Error('What If Error');
	}, []);

	useEffect(() => {
		setInjectedResult(null);
		setShowInjectedResultDialog(false);

		if (id === 'new') {
			setViewMessage(null);
			WhatIfEditService.create().then(resp => {
				setDto(resp);
				const sketch = normalizeSketchFromDto(resp.sketch !== null ? resp.sketch : defaultData, resp);
				setData(sketch);
				setFigure(sketch.figure.find(f => f.name === 'main'));
			});
		} else {
			WhatIfEditService.getData(id).then(resp => {
				setViewMessage(null);
				setDto(resp);

				const sketch = normalizeSketchFromDto(resp.sketch !== null ? resp.sketch : defaultData, resp);

				if (taskId && sketch && sketch.experiment && sketch.experiment.attributes) {

					console.log('Get data for inject: ' + taskId);

					if (taskId[0] === 'E') {
						const experimentId = Number(taskId.match(/\d+/)?.[0]);

						if (experimentId) {
							WhatIfExperimentEditService.getData(experimentId).then(experiment => {
								const mappedExperiment = mapExperimentDto(experiment);
								const mainFigure = sketch.figure.find(f => f.name === 'main');

								setData({
									...defaultData,
									...sketch,
									properties: sketch && sketch.properties ? sketch.properties : null,
									experiment: mappedExperiment
								});

								setFigure(mainFigure);

								setTimeout(() => {
									setExperiment(mappedExperiment);
								}, 0);

								setViewMessage(experiment.name ? `You are currently viewing ${experiment.name} experiment.` : null);
								dispatch(showMessage({ message: 'Experiment loaded successfully' }));
							});
						}
					}

					if (taskId[0] === 'S') {
						const scenarioId = Number(taskId.match(/\d+/)?.[0]);

						if (scenarioId) {
							WhatIfScenarioEditService.getData(scenarioId).then(scenario => {
								const experiment = applyScenarioAttributesToExperiment(sketch.experiment, scenario.attributes);
								const mainFigure = sketch.figure.find(f => f.name === 'main');

								setData({
									...defaultData,
									...sketch,
									properties: sketch && sketch.properties ? sketch.properties : null,
									experiment
								});

								setFigure(mainFigure);
								setViewMessage(getScenarioViewMessage(scenario));
								dispatch(showMessage({ message: 'Scenario loaded successfully' }));
							});
						}
					}

					if (taskId[0] === 'N') {
						NotificationService.getData(Number(taskId.match(/\d+/)?.[0])).then(task => {

							// if (task && task.data && task.data.result && (task.data.result.aspect || task.data.result.transformer || task.data.result.best_objective))
							// 	injectTaskState(sketch, task.data.result);

							const lastResult = task?.data?.lastResult;

							if (lastResult && (lastResult.aspect || lastResult.transformer || lastResult.best_objective)) {
								injectTaskState(sketch, lastResult);
								setInjectedResult(createInjectedResultDialogData({
									taskId,
									type: task?.type || 'Notification',
									name: task?.name || task?.title,
									data: lastResult
								}));
								setViewMessage(getTaskViewMessage(taskId));
							}

							setData(sketch);
							setFigure(sketch.figure.find(f => f.name === 'main'));
							dispatch(showMessage({ message: 'Data loaded successfully' }));

						});
					}

					if (taskId[0] === 'T') {
						const n = Number(taskId.match(/\d+/)?.[0]);

						const isCalc = /^TC/i.test(taskId);
						const service = isCalc ? CalculateTaskEditService/*TaskManagerEditService*/ : OptimizationCaseTaskEditService;

						service.getData(n).then(task => {
							console.log('[Response to inject]', task);

							const bestObjective = task?.lastResult?.best_objective;
							const stateToInject = overrideStateInjectWithState(
								isCalc ? task?.stateInject : bestObjective?.state_inject,
								isCalc ? task?.state : bestObjective?.state
							);
							const lastResult = !isCalc && bestObjective
								? {
									...task?.lastResult,
									best_objective: {
										...bestObjective,
										state_inject: stateToInject
									}
								}
								: task?.lastResult;

							if (stateToInject) {
								injectTaskState(sketch, stateToInject, true);
								setInjectedResult(createInjectedResultDialogData({
									taskId,
									type: isCalc ? 'Calculate' : (task?.type || 'Optimize'),
									name: task?.name,
									data: isCalc ? null : lastResult,
									stateInject: isCalc ? stateToInject : null
								}));
								setViewMessage(getTaskViewMessage(taskId));

							}

							setData(sketch);
							setFigure(sketch.figure.find(f => f.name === 'main'));
							dispatch(showMessage({ message: 'Data loaded successfully' }));
						});
					}

					/* model validation */
					if (taskId[0] === 'M') {
						const m = taskId.match(/^M(\d+)(?:&TS(.+))?$/i);
						const modelValidationLog = m ? Number(m[1]) : null;
						const ts = m ? m[2] || null : null;

						console.log('modelValidationLog', modelValidationLog);
						console.log('TS:', ts);

						if (modelValidationLog) {
							ModelValidationLogTableService.getDataByTs(modelValidationLog, ts).then(list => {

								const stateToInject = list?.completed_state ?? list?.state;

								if (stateToInject) {
									console.log('[State parent]', list);
									console.log('[State to inject]', stateToInject);
									injectTaskState(sketch, stateToInject, true);
									setInjectedResult(createInjectedResultDialogData({
										taskId,
										type: 'ModelValidation',
										data: list,
										stateInject: stateToInject
									}));
									setViewMessage(getTaskViewMessage(taskId));
									dispatch(showMessage({ message: 'State loaded successfully' }));
								} else {
									dispatch(showMessage({ message: 'Completed state not foun' }));
								}

								setData(sketch);
								setFigure(sketch.figure.find(f => f.name === 'main'));
							});
						} else {
							// setUnitOptimizerLogDataList(null);
						}
					}

					/* unit optimier */
					if (taskId[0] === 'O') {
						const m = taskId.match(/^O(\d+)(?:&TS(.+))?$/i);
						const unitOptimizerLog = m ? Number(m[1]) : null;
						const ts = m ? m[2] || null : null;

						console.log('unitOptimizerLog', unitOptimizerLog);
						console.log('TS:', ts);

						if (unitOptimizerLog) {

							UnitOptimizerLogTableService.getDataByTs(unitOptimizerLog, ts).then(data => {

								const state = data?.best_state;

								if (state) {
									console.log('[State to inject]', state);
									injectTaskState(sketch, state, true);
									setInjectedResult(createInjectedResultDialogData({
										taskId,
										type: 'UnitOptimizer',
										data,
										stateInject: state
									}));
									setViewMessage(getTaskViewMessage(taskId));
									dispatch(showMessage({ message: 'State loaded successfully' }));
								} else {
									dispatch(showMessage({ message: 'Completed state not found' }));
								}

								setData(sketch);
								setFigure(sketch.figure.find(f => f.name === 'main'));
							});

							// UnitOptimizerLogTableService.getData(unitOptimizerLog, null, null).then(list => {
							// 	const row = Array.isArray(list) ? list.find(r => r.ts === ts) : null;

							// 	const completedState = row?.details?.aspect?.completed_state;

							// 	if (completedState) {
							// 		console.log('[State parent]', row);
							// 		console.log('[State to inject]', completedState);
							// 		injectTaskState(sketch, completedState, true);
							// 		setViewMessage(getTaskViewMessage(taskId));
							// 		dispatch(showMessage({ message: 'State loaded successfully' }));
							// 	} else {
							// 		dispatch(showMessage({ message: 'Completed state not found' }));
							// 	}

							// 	setData(sketch);
							// 	setFigure(sketch.figure.find(f => f.name === 'main'));
							// });

						} else {
							// setUnitOptimizerLogDataList(null);
						}
					}

				} else {

					if (state && (state.aspect || state.attributes) && sketch && sketch.experiment && sketch.experiment.attributes)
						injectTaskState(sketch, state);

					setData(sketch);
					setFigure(sketch.figure.find(f => f.name === 'main'));

				}
			});
		}
	}, [id, taskId]);

	const handleSave = () => {
		const _dto = {
			...dto,
			sketch: figure
				? {
					...data,
					figure: data.figure.map(f => {
						if (f.name === figure.name) {
							return figure;
						}
						return f;
					})
				}
				: data,
			aspect: data.experiment && data.experiment.aspect ? data.experiment.aspect : null,
			transformer: data.experiment && data.experiment.transformer ? data.experiment.transformer : null,
			optimizationCase: data.properties && data.properties.optimizationCase ? data.properties.optimizationCase : null,
			piMeasurementReader: data.properties && data.properties.piMeasurementReader ? data.properties.piMeasurementReader : null,
			labMeasurementReader: data.properties && data.properties.labMeasurementReader ? data.properties.labMeasurementReader : null,
			measurement: data.properties && data.properties.measurement ? data.properties.measurement : null,
		};

		if (id !== 'new') {
			WhatIfEditService.update(dto.id, _dto).then(resp => handleResponse(resp));
		} else {
			WhatIfEditService.save(_dto).then(resp => handleResponse(resp));
		}
	};

	const setName = name => {
		setDto({ ...dto, name });
	};

	const setGroup = userGroup => {
		setDto({ ...dto, userGroup });
	};

	const handleResponse = resp => {
		if (resp.successful) {
			dispatch(showMessage({ message: t('saveSuccessful') }));
			history.push(mode === 'EXPERT' ? '/what-if-expert' : '/what-if');
		} else {
			dispatch(showMessage({ message: t('saveFailed') }));
		}
	};

	if (dto !== null && data !== null && figure !== null && groupList !== null)
		return (
			<FusePageCarded
				classes={{
					toolbar: '',
					header: '',
					root: classes.root
				}}
				headerAutoHeightBreakpoint="lg"
				innerScroll
				header={
					<div className={'w-full h-full flex flex-col xl:flex-row items-center justify-center ' + (viewMode === 'EDIT' ? 'gap-4 sm:gap-14 xl:gap-16' : 'gap-8')}>

						<NamingAndGroup className={'hidden ' + (viewMode === 'EDIT' ? 'xl:flex' : 'md:flex')} mode={mode} dto={dto} groupList={groupList} setGroup={item => { setGroup(item) }} setName={name => { setName(name) }} hasInjectedResult={!!injectedResult} onShowInjectedResult={() => setShowInjectedResultDialog(true)} />

						{viewMode === 'EDIT' && <WhatIfActions className='xl:flex-1' />}

						<motion.div className='xl:flex-1 flex gap-16 items-center justify-center xl:justify-end' initial={{ opacity: 0, x: 0 }} animate={{ opacity: 1, x: 0, transition: { delay: 0.3 } }}>

							{viewMode === 'EDIT' && <NamingAndGroup className='hidden sm:flex xl:hidden' mode={mode} dto={dto} groupList={groupList} setGroup={item => { setGroup(item) }} setName={name => { setName(name) }} />}

							<div className='flex flex-col md:flex-row gap-8 md:gap-0 items-center justify-center'>
								<div className='flex items-center justify-center'>
									{id !== 'new' && user.role.includes('expert') && <WhatIfViewModeButton />}
									{viewMode === 'EDIT' && <WhatIfProperties />}
									{viewMode === 'EDIT' && <Button className='whitespace-nowrap mx-4' variant='contained' color='secondary' onClick={() => handleSave()} startIcon={<Icon className='hidden sm:flex'>save</Icon>}>{t('save')}</Button>}
									{(viewMode === 'CALCULATE' && _.get(data, 'properties.optimizationCase')) && <WhatIfOptimizationCaseButton name={dto.name} id={id} />}
									{viewMode === 'CALCULATE' && <WhatIfStateButton />}
									{viewMode === 'CALCULATE' && <WhatIfScenario />}
									{viewMode === 'CALCULATE' && <WhatIfExperiment />}
								</div>

								<div className='flex items-center justify-center'>
									{(viewMode === 'CALCULATE'/* && !isOperator*/ && (_.get(data, 'properties.operatorVisibility.connectBtnVisible', 'VISIBLE') === 'VISIBLE')) && (<WhatIfAutoConnect aspect={data.experiment && data.experiment.aspect ? data.experiment.aspect : data.aspect} flg={_.get(data, 'properties.operatorVisibility.connectBtnVisible', 'VISIBLE')} />)}
									{viewMode === 'CALCULATE' /*&& !isOperator*/ && _.get(data, 'properties.operatorVisibility.calculateBtnVisible', true) === true ? <WhatIfCalculate id={id} /> : null}
									{viewMode === 'CALCULATE'/* && !isOperator*/ && _.get(data, 'properties.operatorVisibility.calculateBackgroundBtnVisible', true) === true ? <WhatIfCalculateBackground id={id} /> : null}
								</div>
							</div>

						</motion.div>

					</div>
				}
				content={
					<div className='h-full relative overflow-hidden' ref={containerRef}>
						{dto !== null && data !== null && figure != null && <WhatIfView />}
						<WhatIfDeviceButtons />
						<CompareSidebar />
						{valueModify !== null && <ValueModify containerRef={containerRef} />}
						{outputResultDialog !== null && <OutputResultDialog />}
						{summaryTableDialog !== null && <SumTableDialog />}
						{showInjectedResultDialog && injectedResult && <TaskManagerLastResultDialog item={injectedResult.item} data={injectedResult.data} onClose={() => setShowInjectedResultDialog(false)} />}
						{floatValue !== null && <FloatValue containerRef={containerRef} />}
						{floatLabelCompare !== null && <FloatLabelCompare containerRef={containerRef} />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'CIRCLE' && <CircleItemProperties />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'RECT' && <RectItemProperties />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'DEVICE' && <DeviceItemProperties />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'LABEL' && <LabelItemProperties />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'IMAGE' && <ImageItemProperties />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'SUMTABLE' && <SumTableItemProperties />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'DOCUMENT' && <DocumentItemProperties />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'DOCUMENT_OPERATOR' && <DocumentItemOperator />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'NOTE' && <NoteItemProperties />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'NOTE_OPERATOR' && <NoteItemOperator />}
						{showPropertiesDialog !== null && showPropertiesDialog.type === 'CONTAINER' && <ContainerItemProperties />}
						{linkToWhatIf !== null && <LinkToWhatIfDialog />}
					</div>
				}
			/>
		);
	return <></>;
}

function NamingAndGroup(props) {
	const { viewMessage, viewMode } = useWhatIf();

	const { className, dto, groupList, setGroup, setName, mode, hasInjectedResult, onShowInjectedResult } = props;
	const { t } = useTranslation('whatIf');

	return (
		<div className={'relative min-w-0 xl:flex-1 flex-col items-center xl:items-start justify-center ' + className}>

			<motion.div className='w-full min-w-0 flex flex-row flex-wrap xl:flex-nowrap gap-12 xl:gap-16 items-center justify-center xl:justify-start' initial={{ y: 0 }} animate={{ y: 0, transition: { delay: 0.3 } }}>

				<div className='flex flex-shrink-0 items-center justify-center xl:justify-start'>
					<PageHeaderBackButton
						className='flex items-center mb-0'
						to={mode === 'EXPERT' ? '/what-if-expert' : '/what-if'}
						label={t('back')}
						disableLeftPadding
					/>
				</div>

				<div className='relative min-w-0 flex flex-1 flex-row flex-wrap xl:flex-nowrap gap-12 xl:gap-16 items-center justify-center xl:justify-start'>
					<TextField className='min-w-128 max-w-256 flex-shrink-0' value={dto.name} onChange={e => (e.target.value.length > 0 ? setName(e.target.value) : setName(null))} required label={t('whatIfName')} variant='outlined' />

					<FormControl className='min-w-128 max-w-192 flex-shrink-0' variant='outlined'>

						<InputLabel id='group-label'>User group</InputLabel>

						<Select
							labelId='group-label'
							id='userGroup'
							label='User group'
							value={dto.userGroup}
							defaultValue={null}
							onChange={e => setGroup(e.target.value)}
							renderValue={selected => { return selected ? groupList.find(item => item.id === selected).name : ''; }}
							IconComponent={ArrowDropDown}>

							<MenuItem value={null} />

							{groupList.map((item, i) => (<MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>))}

						</Select>

					</FormControl>

					{(viewMessage && viewMode !== 'EDIT') && (
						<div className='min-w-0 max-w-full flex flex-1 flex-nowrap items-center gap-6 overflow-hidden'>
							<Tooltip title={viewMessage} placement='bottom' arrow>
								<span className="opacity-70 min-w-0 whitespace-nowrap overflow-hidden overflow-ellipsis">{viewMessage}</span>
							</Tooltip>
							{hasInjectedResult ? (
								<Tooltip title='Show injected state' placement='bottom' arrow>
									<IconButton className='p-4 flex-shrink-0' size='small' onClick={onShowInjectedResult}>
										<PiEyeDuotone size={18} />
									</IconButton>
								</Tooltip>
							) : null}
						</div>
					)}

					<div className='hidden xl:flex absolute left-0 top-full mt-10 w-full min-w-0'><FigureWrapComponent mode={mode} absolute /></div>
				</div>

			</motion.div>

			<div className='flex xl:hidden mt-10 w-full justify-center'><FigureWrapComponent mode={mode} /></div>

		</div >
	);
};

function FigureWrapComponent(props) {
	const { mode } = props;

	if (props.absolute)
		return (
			<div className='w-full min-w-0'>
				<motion.div className='w-full min-w-0' initial={{ x: 0, opacity: 0 }} animate={{ x: 0, opacity: 1, transition: { delay: 0.3 } }}>
					<FigureComponent mode={mode} />
				</motion.div>
			</div>
		);

	return (
		<div className='w-full min-w-0'>
			<motion.div className='w-full min-w-0' initial={{ x: 0, opacity: 0 }} animate={{ x: 0, opacity: 1, transition: { delay: 0.3 } }}>
				<div className='flex gap-6 items-center justify-center min-w-0'>
					<FigureComponent mode={mode} />
				</div>
			</motion.div>
		</div>
	);
}

function FigureComponent() {
	return (
		<div className='flex gap-6 items-center min-w-0'>
			<FigurePathComponent />
		</div>
	);
}

export default withRouter(WhatIfEditPage);
